import { useTranslation } from 'react-i18next';

import { ChangePlanModalElement, ConfirmActionModalElement } from '@shared/dream-components';

import { useSubscriptionActionsContext } from '~/context/SubscriptionManagement';

import DowngradeAction from '../../../_actions/DowngradeAction';
import ReportAbuseAction from '../../../_actions/ReportAbuseAction';
import ResubscribeAction from '../../../_actions/ResubscribeAction';
import { default as UnsubscribeAction } from '../../../_actions/UnsubscribeAction';
import ChangePlanAction from '../actions/ChangePlanAction';
import UnpauseAction from '../actions/UnpauseAction';
import { RetainSubscriptionModal } from '../RetainSubscriptionModal';

/**
 * Renders every subscription lifecycle modal — change-plan, downgrade, unsubscribe,
 * resubscribe, report-abuse, and account- / tier-level unpause — by reusing the existing
 * render-prop Action wrappers. All inputs come from `SubscriptionActionsProvider` via context;
 * this component is pure UI.
 */
export const SubscriptionLifecycleModals = () => {
  const { t, i18n } = useTranslation();
  const {
    currentModal,
    closeModal,
    publication,
    subscriberId,
    isLoggedIn,
    accountPauseId,
    tierUnpauseTierId,
    tierUnpausePauseInfoId,
    retentionFlowEnabled,
    isPremiumTier,
    areAllPlansGifts,
    areAllPlansDowngrading,
    unsubscribeMessage,
    showUnsubscribeRetentionModal,
    resubscribeMessage,
    onUnsubscribeSuccess,
    onUnpausePlanSuccess,
    changePlanTierId,
    actionableTier,
    subscriptionPriceIds,
    subscriptionTierIds,
    selectedPriceId,
    onSelectPrice,
    prorationPreview,
    isLoadingProration,
    onChangePlanSuccess,
    downgradeMessage,
    onDowngradeSuccess,
  } = useSubscriptionActionsContext();

  return (
    <>
      {/* change-plan, downgrade, and tier-level unpause are only reachable from the logged-in
          CurrentPlans card; gating their render keeps logged-out users from mounting them. */}
      {isLoggedIn && (
        <>
          <ChangePlanAction onSuccess={onChangePlanSuccess} tierId={changePlanTierId} subscriberId={subscriberId}>
            {({ isLoading, submit }) => (
              <>
                <ChangePlanModalElement
                  element={{
                    type: 'changePlanModal',
                    attrs: {
                      data: {
                        tier: actionableTier,
                        subscribedToPriceIds: subscriptionPriceIds,
                        subscribedToTierIds: subscriptionTierIds,
                      },
                    },
                  }}
                  isLoading={isLoading}
                  isOpen={currentModal === 'change_plan'}
                  onCancel={closeModal}
                  // SimpleModal fires onClose at the end of its exit animation even when the
                  // close was triggered programmatically (e.g. selecting a price opens the
                  // proration modal). Only close if change_plan is still the active modal so we
                  // don't clobber the proration_confirm modal that just opened.
                  onOpenChange={(_o: boolean) => {
                    if (currentModal === 'change_plan') closeModal();
                  }}
                  onSelectPrice={onSelectPrice}
                  translations={{
                    'shared.cta.cancel': t('shared.cta.cancel'),
                    'billing.plan.change_plan': t('billing.plan.change_plan'),
                    'billing.plans_modal.current_plan_button': t('billing.plans_modal.current_plan_button'),
                    'billing.plans_modal.switch_to_plan_button': t('billing.plans_modal.switch_to_plan_button'),
                  }}
                >
                  {null}
                </ChangePlanModalElement>
                <ConfirmActionModalElement
                  element={{ type: 'confirmActionModal' }}
                  title={t('billing.plans_modal.proration_confirm_title')}
                  message={
                    prorationPreview
                      ? t('billing.plans_modal.proration_message', {
                          amount: new Intl.NumberFormat(i18n.language, {
                            style: 'currency',
                            currency: prorationPreview.currency.toUpperCase(),
                          }).format(prorationPreview.amount_due / 100),
                        })
                      : undefined
                  }
                  submitLabel={t('billing.plans_modal.confirm_change_button')}
                  cancelLabel={t('shared.cta.cancel')}
                  isLoading={isLoading || isLoadingProration || !prorationPreview}
                  isOpen={currentModal === 'proration_confirm'}
                  onCancel={closeModal}
                  onOpenChange={(_o: boolean) => closeModal()}
                  onSubmit={() => selectedPriceId && submit(selectedPriceId)}
                  isSubmitting={isLoading}
                >
                  {null}
                </ConfirmActionModalElement>
              </>
            )}
          </ChangePlanAction>

          <DowngradeAction
            successToastMessage={t('account_actions.downgrade.toast.cancelled')}
            showSubmitButton={false}
            onSuccess={onDowngradeSuccess}
            tier={actionableTier}
          >
            {({ isLoading, submitForm }) =>
              retentionFlowEnabled ? (
                <RetainSubscriptionModal
                  isUnsubscribing={false}
                  isDowngrading={isLoading}
                  isOpen={currentModal === 'downgrade'}
                  closeModal={closeModal}
                  onSuccess={onDowngradeSuccess}
                  mode="downgrade"
                  isPremiumSubscription={isPremiumTier}
                  showPause={retentionFlowEnabled}
                  tier={actionableTier}
                  areAllPlansDowngrading={false}
                  areAllPlansGifts={false}
                  subscriberId={subscriberId}
                />
              ) : (
                <ConfirmActionModalElement
                  element={{ type: 'confirmActionModal' }}
                  title={t('manage.downgrade.title')}
                  message={downgradeMessage}
                  submitLabel={t('shared.cta.cancel_paid_subscription')}
                  cancelLabel={t('shared.cta.cancel')}
                  isSubmitting={isLoading}
                  isOpen={currentModal === 'downgrade'}
                  onCancel={closeModal}
                  onOpenChange={(_o: boolean) => closeModal()}
                  onSubmit={() => {
                    submitForm();
                    closeModal();
                  }}
                >
                  {null}
                </ConfirmActionModalElement>
              )
            }
          </DowngradeAction>

          <UnpauseAction
            pauseId={tierUnpausePauseInfoId}
            subscriberId={subscriberId}
            tierId={tierUnpauseTierId}
            onSuccess={onUnpausePlanSuccess}
          >
            {({ isLoading, submitForm }) => (
              <ConfirmActionModalElement
                element={{ type: 'confirmActionModal' }}
                title={t('manage.unpause.title')}
                cancelLabel={t('shared.cta.cancel')}
                submitLabel={t('shared.cta.unpause')}
                message={t('manage.unpause_tier.message')}
                isSubmitting={isLoading}
                isOpen={currentModal === 'unpause_plan'}
                onCancel={closeModal}
                onOpenChange={(_o: boolean) => closeModal()}
                onSubmit={submitForm}
              >
                {null}
              </ConfirmActionModalElement>
            )}
          </UnpauseAction>
        </>
      )}

      <UnsubscribeAction publication={publication} showSubmitButton={false} onSuccess={onUnsubscribeSuccess}>
        {({ isLoading, submitForm }) => {
          if (!showUnsubscribeRetentionModal) {
            return (
              <ConfirmActionModalElement
                element={{ type: 'confirmActionModal' }}
                title={t('manage.downgrade.title')}
                cancelLabel={t('shared.cta.cancel')}
                submitLabel={t('shared.cta.unsubscribe')}
                message={unsubscribeMessage}
                isSubmitting={isLoading}
                isOpen={currentModal === 'unsubscribe'}
                onCancel={closeModal}
                onOpenChange={(_o: boolean) => closeModal()}
                onSubmit={submitForm}
              >
                {null}
              </ConfirmActionModalElement>
            );
          }

          return (
            <RetainSubscriptionModal
              isUnsubscribing={isLoading}
              isDowngrading={false}
              isOpen={currentModal === 'unsubscribe'}
              closeModal={closeModal}
              onSuccess={onUnsubscribeSuccess}
              mode="unsubscribe"
              isPremiumSubscription={isPremiumTier}
              areAllPlansDowngrading={areAllPlansDowngrading}
              areAllPlansGifts={areAllPlansGifts}
              showPause={retentionFlowEnabled}
              tier={actionableTier}
              subscriberId={subscriberId}
            />
          );
        }}
      </UnsubscribeAction>

      <ResubscribeAction showSubmitButton={false} onSuccess={closeModal}>
        {({ isLoading, submitForm }) => (
          <ConfirmActionModalElement
            element={{ type: 'confirmActionModal' }}
            title={t('manage.downgrade.title')}
            cancelLabel={t('shared.cta.cancel')}
            submitLabel={t('shared.cta.resubscribe')}
            message={resubscribeMessage}
            isSubmitting={isLoading}
            isOpen={currentModal === 'resubscribe'}
            onCancel={closeModal}
            onOpenChange={(_o: boolean) => closeModal()}
            onSubmit={submitForm}
          >
            {null}
          </ConfirmActionModalElement>
        )}
      </ResubscribeAction>

      <ReportAbuseAction
        onSuccess={onUnsubscribeSuccess}
        successMessage={t('account_actions.report_abuse.toast.success')}
      >
        {({ isLoading, submitForm }) => (
          <ConfirmActionModalElement
            element={{ type: 'confirmActionModal' }}
            title={t('manage.report_abuse.title')}
            cancelLabel={t('shared.cta.cancel')}
            submitLabel={t('manage.report_abuse.submit')}
            message={t('manage.report_abuse.message')}
            isSubmitting={isLoading}
            isOpen={currentModal === 'report_abuse'}
            onCancel={closeModal}
            onOpenChange={(_o: boolean) => closeModal()}
            onSubmit={submitForm}
          >
            {null}
          </ConfirmActionModalElement>
        )}
      </ReportAbuseAction>

      <UnpauseAction pauseId={accountPauseId} subscriberId={subscriberId} onSuccess={closeModal}>
        {({ isLoading, submitForm }) => (
          <ConfirmActionModalElement
            element={{ type: 'confirmActionModal' }}
            title={t('manage.unpause.title')}
            cancelLabel={t('shared.cta.cancel')}
            submitLabel={t('shared.cta.unpause')}
            message={t('manage.unpause.message')}
            isSubmitting={isLoading}
            isOpen={currentModal === 'unpause'}
            onCancel={closeModal}
            onOpenChange={(_o: boolean) => closeModal()}
            onSubmit={submitForm}
          >
            {null}
          </ConfirmActionModalElement>
        )}
      </UnpauseAction>
    </>
  );
};
